import { type ComponentPropsWithoutRef, forwardRef, memo, type ReactNode } from 'react';

import { Command } from '@air/cmdk';
import { ImageSearchIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import { usePrivateSearchUIHelpers } from '~/components/Search/hooks/usePrivateSearchUIHelpers';
import { searchFieldIconSlot, searchFieldText } from '~/components/Search/searchFieldChrome';
import { type SearchCommandFilterCountProps } from '~/components/SearchCommand/SearchCommandFilterCount';
import { SearchCommandInputPlaceholder } from '~/components/SearchCommand/SearchCommandInputPlaceholder';
import { SearchCommandModeSelector } from '~/components/SearchCommand/SearchCommandModeSelector';
import { SearchCommandVisualSearchButton } from '~/components/SearchCommand/SearchCommandVisualSearchButton';
import { useAppliedFiltersCount } from '~/hooks/filters/useAppliedFiltersCount';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';

export type SmartSearchCommandInputProps = Pick<
  ComponentPropsWithoutRef<typeof Command.Input>,
  'className' | 'defaultValue' | 'onValueChange' | 'placeholder' | 'value'
> &
  Partial<Pick<SearchCommandFilterCountProps, 'count'>> & {
    children?: ReactNode;
    compact?: boolean;
    onVisualSearchClick: (() => void) | undefined;
    visualSearchTooltip: string;
  };

export const SmartSearchCommandInput = memo(
  forwardRef<HTMLInputElement, SmartSearchCommandInputProps>(
    (
      {
        children,
        className,
        compact,
        defaultValue,
        onValueChange,
        onVisualSearchClick,
        placeholder,
        value,
        visualSearchTooltip,
      }: SmartSearchCommandInputProps,
      ref,
    ) => {
      const { similarToImageSearchAvailable, isActiveImageSearch } = usePrivateSearchUIHelpers();
      const { filtersCanBeSaved } = useAppliedFiltersCount();
      const { ui2026 } = useTemporaryFeatureFlags();
      const canShowVisualSearchAffordance =
        similarToImageSearchAvailable && !value && !isActiveImageSearch && !filtersCanBeSaved;
      const shouldShowVisualSearchButton = canShowVisualSearchAffordance && !!onVisualSearchClick;
      const shouldShowVisualSearchIcon = canShowVisualSearchAffordance && !onVisualSearchClick;

      return (
        <div
          className={cn(
            'relative flex shrink-0 overflow-hidden border border-transparent outline-none',
            ui2026 && 'h-full min-h-0 w-full items-stretch gap-1 px-1',
            !ui2026 && '-m-px items-center gap-2 rounded-8 py-0 pr-3 pl-2',
            !ui2026 && (compact ? 'h-8' : 'h-10'),
            className,
          )}
          data-testid="SEARCH_COMMAND_INPUT"
        >
          <div className={cn('relative order-2 min-w-0 flex-1 self-stretch', ui2026 && 'flex items-center py-1')}>
            <Command.Input
              className={cn(
                searchFieldText(),
                ui2026 ? 'h-auto min-h-0 w-full' : 'size-full rounded-8',
                compact ? 'bg-transparent pr-10 text-14 placeholder:text-transparent' : undefined,
                className,
              )}
              data-testid="SEARCH_COMMAND_INPUT_INPUT"
              defaultValue={defaultValue}
              onValueChange={onValueChange}
              placeholder={placeholder}
              ref={ref}
              value={value}
            />
            {compact && !value && placeholder && (
              <SearchCommandInputPlaceholder className="left-0" placeholder={placeholder} />
            )}
          </div>
          <span className="order-1 flex shrink-0 items-center self-center" data-testid="SEARCH_COMMAND_LEADING_SLOT">
            <SearchCommandModeSelector search={value} showTooltip={!ui2026} />
          </span>
          {!ui2026 && !!value && <span aria-hidden className="pointer-events-none order-3 w-5 min-w-5 shrink-0" />}
          {shouldShowVisualSearchIcon && (
            <span
              className={cn('order-3 ml-auto', ui2026 && searchFieldIconSlot())}
              data-testid="SEARCH_COMMAND_TRAILING_SLOT"
            >
              <ImageSearchIcon
                className="shrink-0 ads:text-icon-default text-gray-10"
                data-testid="SEARCH_COMMAND_IMAGE_SEARCH_ICON"
                size="sm"
              />
            </span>
          )}
          {children ? (
            <span className="order-last flex shrink-0 items-center gap-2 ui2026:gap-1">{children}</span>
          ) : null}
          {shouldShowVisualSearchButton && onVisualSearchClick && (
            <span className="order-last ml-auto flex shrink-0 items-center" data-testid="SEARCH_COMMAND_TRAILING_SLOT">
              <SearchCommandVisualSearchButton onClick={onVisualSearchClick} tooltip={visualSearchTooltip} />
            </span>
          )}
        </div>
      );
    },
  ),
);

SmartSearchCommandInput.displayName = 'SmartSearchCommandInput';
